import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from 'react';
import { useDispatch } from 'react-redux';
import { Button, Checkbox, Money, Spinner, Text } from '@belong/ui';
import type {
  PaymentAccountResourceModel,
  PaymentMethodFee,
  PaymentMethodFeeList,
} from 'api/models';
import type { GroupedBalancesInfoModel } from 'api/models.v2';
import { ApiClientContext } from 'common/contexts/api-client';
import { PAYMENT_SCREENS } from 'containercomponents/Modals/PaymentsModal/PaymentsModal';
import PaymentMethodSelectorContainer from 'containers/PaymentMethodSelectorContainer/PaymentMethodSelectorContainer';
import { createBillsPayment } from 'store/redux/accounts/actions';
import { showToast } from 'store/redux/toast';
import { FEEDBACK_STRINGS } from 'strings/errors.string';
import {
  getBalanceIds,
  getDefaultPaymentMethod,
  getFeeLabel,
  getMatchingFee,
  getPayInAdvanceBalances,
  isMaintenanceBalance,
  shouldShowFee,
} from './pay-now-utils';
import { PAY_NOW_STRINGS } from './pay-now-strings';
import { SelectableBalance } from './selectable-balance';
import { PayInAdvanceBanner } from './pay-in-advance-banner';

type PayNowContentProps = {
  balances: GroupedBalancesInfoModel[];
  paymentMethods: PaymentAccountResourceModel[];
  isHomeowner: boolean;
  onPaymentComplete: (result: {
    success: boolean;
    errorMessage?: string;
  }) => void;
  renderFooter?: (props: {
    onPayNowClick: () => void;
    isDisabled: boolean;
    isLoading: boolean;
  }) => React.ReactNode;
};

export function PayNowContent({
  balances,
  paymentMethods,
  isHomeowner,
  onPaymentComplete,
  renderFooter,
}: PayNowContentProps) {
  const dispatch = useDispatch();
  const apiClient = useContext(ApiClientContext);

  const payableBalances = useMemo(
    () => balances.filter((b) => b.canBePaid && b.category === 'Expenses'),
    [balances],
  );

  const [selectedBalanceIds, setSelectedBalanceIds] = useState<Set<number>>(
    () => new Set(payableBalances.map((b) => b.groupBalanceItemId)),
  );
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);
  const [billTotal, setBillTotal] = useState<number | null>(null);
  const [billFees, setBillFees] = useState<PaymentMethodFee[] | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasApiError, setHasApiError] = useState(false);

  const selectedBalances = useMemo(
    () =>
      payableBalances.filter((b) =>
        selectedBalanceIds.has(b.groupBalanceItemId),
      ),
    [payableBalances, selectedBalanceIds],
  );
  const advanceBalances = useMemo(
    () => getPayInAdvanceBalances(selectedBalances),
    [selectedBalances],
  );

  const balanceIdsForApi = useMemo(
    () => getBalanceIds(selectedBalances),
    [selectedBalances],
  );

  // Stable key for the effect dependency
  const balanceIdsKey = balanceIdsForApi.join(',');

  const isAllMaintenance = useMemo(
    () =>
      selectedBalances.length > 0 &&
      selectedBalances.every((b) => isMaintenanceBalance(b)),
    [selectedBalances],
  );

  const verifiedMethods = useMemo(
    () => paymentMethods.filter((pm) => pm.isVerified),
    [paymentMethods],
  );

  const selectedPaymentMethod = useMemo(
    () => paymentMethods.find((pm) => pm.accountId === selectedPaymentMethodId),
    [paymentMethods, selectedPaymentMethodId],
  );

  // Auto-select default payment method
  useEffect(() => {
    if (verifiedMethods.length > 0 && !selectedPaymentMethodId) {
      const defaultMethod = getDefaultPaymentMethod(
        verifiedMethods,
        isAllMaintenance,
        isHomeowner,
      );
      if (defaultMethod) {
        // `accountId` is optional on the model; the state already admits
        // "none selected", so `?? null` keeps that meaning.
        setSelectedPaymentMethodId(defaultMethod.accountId ?? null);
      }
    }
  }, [verifiedMethods, selectedPaymentMethodId, isAllMaintenance, isHomeowner]);

  // Fetch bill total when selected balances change
  useEffect(() => {
    if (balanceIdsForApi.length === 0) {
      setBillTotal(null);
      setBillFees(null);
      setHasApiError(false);
      return;
    }

    let cancelled = false;
    setIsCalculating(true);
    setHasApiError(false);

    async function fetchTotalAndFees() {
      try {
        const result: { amount: number } = await apiClient.post(
          '/users/bills/total',
          { balances: balanceIdsForApi },
        );
        if (cancelled) return;
        setBillTotal(result.amount);

        if (result.amount > 0) {
          const feesResult: PaymentMethodFeeList = await apiClient.get(
            `/users/bills/fees?amount=${result.amount}`,
          );
          if (cancelled) return;
          setBillFees(feesResult?.fees ?? null);
        }
      } catch {
        if (cancelled) return;
        setHasApiError(true);
      } finally {
        if (!cancelled) {
          setIsCalculating(false);
        }
      }
    }

    fetchTotalAndFees();

    return () => {
      cancelled = true;
    };
  }, [apiClient, balanceIdsKey]);

  const matchingFee = useMemo(() => {
    if (!billFees || !selectedPaymentMethod) return undefined;
    return getMatchingFee(billFees, selectedPaymentMethod);
  }, [billFees, selectedPaymentMethod]);

  const showFee = selectedPaymentMethod
    ? shouldShowFee(selectedPaymentMethod, matchingFee)
    : false;

  const feeAmount = showFee && matchingFee ? matchingFee.feeAmount : 0;

  const totalWithFee =
    showFee && matchingFee ? matchingFee.totalWithFee : (billTotal ?? 0);

  const paymentModalProps = {
    screen: PAYMENT_SCREENS.PAYMENT_SELECTION,
    errorScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.error.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.error.ctaLabel,
    },
    successScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.success.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.success.ctaLabel,
    },
  };

  const isPayNowDisabled =
    !selectedPaymentMethod ||
    selectedBalances.length === 0 ||
    isCalculating ||
    hasApiError;

  const handleToggleBalance = useCallback(
    (balance: GroupedBalancesInfoModel) => {
      setSelectedBalanceIds((prev) => {
        const next = new Set(prev);
        if (next.has(balance.groupBalanceItemId)) {
          next.delete(balance.groupBalanceItemId);
        } else {
          next.add(balance.groupBalanceItemId);
        }
        return next;
      });
    },
    [],
  );

  async function handlePayNowClick() {
    if (!selectedPaymentMethod?.accountId) return;
    setIsSubmitting(true);
    try {
      await dispatch(
        createBillsPayment({
          paymentAccountUniqueId: selectedPaymentMethod.accountId,
          balances: balanceIdsForApi,
        }),
      );
      dispatch(
        showToast(
          { message: PAY_NOW_STRINGS['payment.success'], success: true },
          { autoDelete: true, autoDeleteTime: 4000 },
        ),
      );
      onPaymentComplete({ success: true });
    } catch (error: unknown) {
      const err = error as Record<string, unknown>;
      const dataArr = err?.data as Array<{ message?: string }> | undefined;
      const message =
        dataArr?.[0]?.message ??
        (err?.message as string) ??
        PAY_NOW_STRINGS['payment.error'];
      dispatch(
        showToast(
          { message, error: true },
          { autoDelete: true, autoDeleteTime: 5000 },
        ),
      );
      onPaymentComplete({ success: false, errorMessage: message });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isSubmitting) {
    return (
      <div className="flex items-center justify-center py-2xl">
        <Spinner />
      </div>
    );
  }

  if (payableBalances.length === 0) {
    return (
      <div className="flex flex-col items-center gap-md py-2xl">
        <Text className="text-dark-gray">
          {PAY_NOW_STRINGS['pay_now.no_balances']}
        </Text>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="flex w-full flex-col gap-lg pt-lg">
          <div className="flex flex-col gap-2xs">
            {payableBalances.map((balance) => (
              <SelectableBalance
                key={balance.groupBalanceItemId}
                balance={balance}
                isSelected={selectedBalanceIds.has(balance.groupBalanceItemId)}
                onToggle={handleToggleBalance}
              />
            ))}
            {showFee && matchingFee && selectedPaymentMethod && (
              <div className="flex items-center justify-between rounded-small border border-light-gray py-xs">
                <Checkbox
                  id="balance-fee"
                  checked
                  disabled
                  label={getFeeLabel(selectedPaymentMethod.paymentMethod)}
                  onChange={() => {}}
                />
                <Money
                  value={feeAmount}
                  format="DOLLARS_WITH_CENTS"
                  className="text-body font-semibold"
                />
              </div>
            )}
          </div>

          <div className="border-0 border-t border-dashed border-gray" />

          {hasApiError && selectedBalances.length > 0 ? (
            <div className="rounded-small bg-red-translucent-light p-sm">
              <Text className="text-red">
                {PAY_NOW_STRINGS['payment.error']}
              </Text>
            </div>
          ) : isCalculating && selectedBalances.length > 0 ? (
            <div className="flex items-center justify-center py-md">
              <Spinner />
            </div>
          ) : (
            selectedBalances.length > 0 && (
              <div className="flex flex-col gap-xs">
                <div className="flex items-center justify-between">
                  <Text fontWeight="semibold" variant="h3">
                    {PAY_NOW_STRINGS['pay_now.total']}
                  </Text>
                  <Money
                    value={totalWithFee}
                    format="DOLLARS_WITH_CENTS"
                    className="text-h3-sm font-semibold"
                  />
                </div>
              </div>
            )
          )}

          <div className="my-lg">
            <Text fontWeight="semibold" className="mb-sm">
              {PAY_NOW_STRINGS['payment_methods.title']}
            </Text>
            <PaymentMethodSelectorContainer
              needsPaymentMethod
              selectedPaymentMethodId={selectedPaymentMethodId ?? ''}
              onSelectPaymentMethod={(id: string) =>
                setSelectedPaymentMethodId(id)
              }
              showOnlyValidPaymentMethods
              paymentModalProps={paymentModalProps}
              hasFee
            />
          </div>

          <PayInAdvanceBanner advanceBalances={advanceBalances} />
        </div>

        {renderFooter ? (
          renderFooter({
            onPayNowClick: handlePayNowClick,
            isDisabled: isPayNowDisabled,
            isLoading: isSubmitting,
          })
        ) : (
          <Button
            size="fluid"
            onClick={handlePayNowClick}
            disabled={isPayNowDisabled || isSubmitting}
          >
            {PAY_NOW_STRINGS['pay_now.button']}
          </Button>
        )}
      </div>
    </>
  );
}
